/**
 * L6a: Gemeinsame Hilfen für Rechnungsliste (BillingPage) und Entwurfsseite (InvoiceDraftPage).
 * Aus BillingPage.js verschoben, Logik unverändert.
 */
import { format, isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';

// Default billing permissions
export const DEFAULT_PERMISSIONS = {
  create: ['accounting'],
  edit: ['accounting'],
  approve: ['supervisor'],
  issue: ['accounting'],
  send: ['accounting'],
  void: ['accounting'],
  delete: ['accounting']
};

// Permission check helper
export const mayDo = (user, permissions, action) => {
  if (user?.role === 'admin') return true;
  return ((permissions || DEFAULT_PERMISSIONS)[action] || []).includes(user?.role);
};

// Helper: Format currency
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
};

// Safe date formatting helper
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
    return isValid(date) ? format(date, 'dd.MM.yyyy', { locale: de }) : '-';
  } catch {
    return '-';
  }
};

// Get reference field label based on type
export const getReferenceLabel = (type) => {
  const labels = { po: 'Bestellnummer / PO', contract: 'Vertragsnummer', quote: 'Angebotsnummer' };
  return labels[type] || 'Referenz';
};

// Status badge with all stati
export const getStatusBadge = (invoice) => {
  const status = invoice.status;
  const config = {
    draft: { label: 'Entwurf', className: 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30' },
    approved: { label: 'Freigegeben', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    issued: { label: 'Ausgestellt', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    sent: { label: 'Versendet', className: 'bg-violet-500/20 text-violet-400 border-violet-500/30' },
    void: { label: 'Storniert', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30 line-through' },
    voided: { label: 'Storniert', className: 'bg-gray-500/20 text-gray-400 border-gray-500/30 line-through' },
  };
  const { label, className } = config[status] || config.draft;
  return (
    <span className={`px-2 py-0.5 text-xs rounded border ${className}`}>
      {label}
    </span>
  );
};

// Get provenance info for invoice
export const getProvenanceInfo = (invoice) => {
  const parts = [];
  if (invoice.created_by_name) {
    parts.push(`Erstellt von ${invoice.created_by_name}`);
  }
  if (invoice.approved_by_name && invoice.approved_at) {
    parts.push(`Freigegeben von ${invoice.approved_by_name} am ${formatDate(invoice.approved_at)}`);
  }
  if (invoice.issued_by_name && invoice.issued_at) {
    parts.push(`Ausgestellt von ${invoice.issued_by_name} am ${formatDate(invoice.issued_at)}`);
  }
  if (invoice.sent_at) {
    parts.push(`Versendet am ${formatDate(invoice.sent_at)}`);
  }
  return parts.join(' · ');
};

// API error handler for 409 and 403
export const handleApiError = (error) => {
  const status = error.response?.status;
  let detail = error.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    detail = detail.message || (Array.isArray(detail) ? detail.map((d) => d.msg).join(', ') : JSON.stringify(detail));
  }

  if (status === 409 || status === 403) {
    toast.error(detail || 'Aktion nicht erlaubt');
  } else {
    toast.error(detail || 'Fehler beim Ausführen der Aktion');
  }
};

// X8: XML der E-Rechnung herunterladen (ausgestellt: abgelegte Datei; Entwurf: Vorschau)
export const downloadEinvoiceXml = async (api, invoice) => {
  try {
    const response = await api.get(`/billing/invoices/${invoice.id}/einvoice-xml`, { responseType: 'blob' });
    const match = (response.headers['content-disposition'] || '').match(/filename="?([^";]+)"?/);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([response.data], { type: 'application/xml' }));
    link.download = match ? match[1] : 'rechnung.xml';
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  } catch (error) {
    if (error.response?.data instanceof Blob) {
      try {
        const text = JSON.parse(await error.response.data.text());
        toast.error(typeof text.detail === 'string' ? text.detail : 'XML nicht verfügbar');
        return;
      } catch (e) { /* unten */ }
    }
    handleApiError(error);
  }
};
